/* Chercher une référence dans un catalogue qui en compte des dizaines.
 *
 * À quatre-vingts références par usine, un filtre « le nom contient » ne suffit
 * plus : on tape « huile 1l » et on veut la ligne, sans se soucier de l'ordre
 * des mots ni de ce qui les sépare. D'où la recherche par jetons : chaque mot
 * tapé doit se retrouver quelque part dans la ligne, n'importe où.
 *
 * Rien ici ne touche à l'écran. C'est de la sélection pure, donc éprouvable.
 */

export interface Reference {
  id: string;
  ref: string;
  nom: string;
  gamme: string | null;
  conditionnement: string | null;
  actif: boolean;
  usine_id: string;
}

/** Sans gamme, la référence se range quand même : on ne la perd pas. */
export const SANS_GAMME = "Sans gamme";

/* Les accents ne doivent pas départager : « crème » se trouve en tapant
   « creme », et l'inverse. */
function aplat(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Tout ce dans quoi la recherche a le droit de piocher, mis à plat. */
function botte(r: Reference): string {
  return aplat([r.ref, r.nom, r.gamme ?? "", r.conditionnement ?? ""].join(" "));
}

/** Les mots tapés, à la file. Les séparateurs ne comptent pas. */
export function jetons(q: string): string[] {
  return aplat(q)
    .split(/[^a-z0-9.,]+/)
    .filter(Boolean);
}

export function correspond(r: Reference, q: string): boolean {
  const mots = jetons(q);
  if (!mots.length) return true;
  const dans = botte(r);
  return mots.every((m) => dans.includes(m));
}

export interface Critères {
  usineId: string;
  q?: string;
  gamme?: string | null;
  /** Ce qui est déjà au devis sort de la liste : on ne l'ajoute pas deux fois. */
  exclure?: ReadonlySet<string>;
}

/**
 * Les références proposables, dans l'ordre où on veut les lire : par gamme,
 * puis par référence. Aucune coupe — à quatre-vingts lignes, en masquer la
 * moitié ne simplifie rien, ça cache.
 */
export function filtrer<T extends Reference>(liste: readonly T[], c: Critères): T[] {
  const gamme = c.gamme ?? null;
  return liste
    .filter((r) => r.usine_id === c.usineId && r.actif)
    .filter((r) => !c.exclure?.has(r.id))
    .filter((r) => gamme == null || (r.gamme ?? SANS_GAMME) === gamme)
    .filter((r) => correspond(r, c.q ?? ""))
    .sort(
      (a, b) =>
        (a.gamme ?? SANS_GAMME).localeCompare(b.gamme ?? SANS_GAMME, "fr") ||
        a.ref.localeCompare(b.ref, "fr", { numeric: true }),
    );
}

export interface Paquet<T> {
  gamme: string;
  refs: T[];
}

/** Regroupe une liste déjà triée. L'ordre d'arrivée fait l'ordre des paquets. */
export function grouper<T extends Reference>(liste: readonly T[]): Paquet<T>[] {
  const out: Paquet<T>[] = [];
  for (const r of liste) {
    const g = r.gamme ?? SANS_GAMME;
    const dernier = out[out.length - 1];
    if (dernier && dernier.gamme === g) dernier.refs.push(r);
    else out.push({ gamme: g, refs: [r] });
  }
  return out;
}

/** Les gammes présentes chez cette usine, pour les pastilles de filtre. */
export function gammes<T extends Reference>(
  liste: readonly T[],
  usineId: string,
): { nom: string; n: number }[] {
  const compte = new Map<string, number>();
  for (const r of liste) {
    if (r.usine_id !== usineId || !r.actif) continue;
    const g = r.gamme ?? SANS_GAMME;
    compte.set(g, (compte.get(g) ?? 0) + 1);
  }
  return [...compte.entries()]
    .map(([nom, n]) => ({ nom, n }))
    .sort((a, b) => a.nom.localeCompare(b.nom, "fr"));
}

/* ──────────────────────────────── les articles ─────────────────────────────
 *
 * Un même article se vend en plusieurs formats, et chaque format est une
 * référence à part : « Cumin En Grains » existe en pot 100 g, en sachet, en
 * sac de 1 kg et en tube. Lister les quatre à la suite, c'est faire défiler
 * quatre fois le même produit ; les rassembler sous leur nom et montrer les
 * formats côte à côte, c'est à la fois plus court à l'écran et plus proche du
 * geste réel — on choisit un article, puis son conditionnement.
 *
 * Le nom porte le format, et la gamme le nomme : « Cumin En Grains Sac 1 Kg »
 * avec la gamme « Sac 1 kg ». On retire donc l'un de l'autre, puis la mesure
 * qui traîne au bout, jusqu'à ce que plus rien ne parte.
 *
 * On ne rapproche QUE ce qui porte exactement le même nom une fois le format
 * ôté. Deux orthographes voisines — « Grains » et « Graines » — restent deux
 * articles : rapprocher à vue de nez finirait par mettre dans un devis le
 * produit d'à côté, et une coquille visible dans la liste se corrige, alors
 * qu'un rapprochement silencieux ne se voit jamais.
 */

/* Comparer des caractères ne suffit pas : la base écrit « Pot 100 Gr » dans
   le nom et « Pot 100 g » dans la gamme. On compare donc des jetons ramenés à
   une forme commune — minuscules, sans accent, « gr » rendu à « g », la
   virgule décimale au point. */

const UNITES = new Set(["g", "kg", "ml", "cl", "l"]);

function sansAccent(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function jeton(mot: string): string {
  const m = sansAccent(mot).toLowerCase().replace(/[.,;:]+$/, "").replace(",", ".");
  return m === "gr" || m === "grs" ? "g" : m === "kgs" ? "kg" : m;
}

function decouper(s: string): { mot: string; debut: number; cle: string }[] {
  const out: { mot: string; debut: number; cle: string }[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) out.push({ mot: m[0], debut: m.index, cle: jeton(m[0]) });
  return out;
}

/** « 100 g » en deux jetons, « 100g » en un seul : les deux sont une mesure. */
function mesureALaFin(jetons: { cle: string }[]): number {
  const n = jetons.length;
  const dernier = jetons[n - 1]?.cle ?? "";
  if (n >= 2 && UNITES.has(dernier) && /^\d+(\.\d+)?$/.test(jetons[n - 2]?.cle ?? "")) return 2;
  if (n >= 1 && /^\d+(\.\d+)?(g|kg|ml|cl|l)$/.test(dernier)) return 1;
  return 0;
}

/**
 * Le nom de l'article : celui de la référence, moins son format.
 *
 * On retire la gamme si elle termine le nom, puis la mesure qui traîne au
 * bout, et on recommence tant que quelque chose part — « Tubo 330 Ml 110 G »
 * demande les deux, dans cet ordre-là.
 */
export function nomArticle(nom: string, gamme: string | null): string {
  let n = nom.trim();
  const cles = gamme ? decouper(gamme).map((x) => x.cle) : [];

  for (let tour = 0; tour < 6; tour++) {
    const avant = n;
    let jetons = decouper(n);

    if (cles.length && jetons.length >= cles.length) {
      const fin = jetons.slice(jetons.length - cles.length);
      if (fin.every((x, k) => x.cle === cles[k])) {
        n = n.slice(0, fin[0]?.debut ?? n.length).trim();
        jetons = decouper(n);
      }
    }

    const combien = mesureALaFin(jetons);
    if (combien) n = n.slice(0, jetons[jetons.length - combien]?.debut ?? n.length).trim();

    n = n.replace(/[\u2014\u2013,·-]+$/, "").trim();
    if (n === avant) break;
  }

  /* Si tout est parti, le format EST le produit : on garde le nom entier
     plutôt que de rendre une ligne sans titre. */
  return n || nom.trim();
}

export interface Article<T> {
  /** Le nom sans le format, tel qu'il s'affiche. */
  nom: string;
  /** Les références de cet article, un format chacune. */
  formats: T[];
}

/**
 * Rassemble des références par article, dans l'ordre alphabétique du nom.
 * Les formats d'un article gardent l'ordre de la liste reçue.
 */
export function grouperArticles<T extends Reference>(liste: readonly T[]): Article<T>[] {
  const par = new Map<string, Article<T>>();
  for (const r of liste) {
    const nom = nomArticle(r.nom, r.gamme);
    const cle = sansAccent(nom).toLowerCase();
    const deja = par.get(cle);
    if (deja) deja.formats.push(r);
    else par.set(cle, { nom, formats: [r] });
  }
  return [...par.values()].sort((a, b) => a.nom.localeCompare(b.nom, "fr"));
}

/**
 * Comment nommer un format sur une pastille.
 *
 * La gamme est le vrai conditionnement — « Pot 100 g », « Sachet », « Sac
 * 1 kg » —, tandis que la colonne `conditionnement` porte le poids net. Chez
 * Aromasol, le pot et le sachet pèsent tous deux 100 g : les étiqueter par le
 * poids donnait deux pastilles identiques pour deux produits différents, ce
 * qui est exactement la faute qu'on ne rattrape pas une fois le devis parti.
 *
 * On affiche donc la gamme, et le poids seulement quand elle ne le dit pas
 * déjà — « Sachet · 100 g », mais « Pot 100 g » tout court.
 */
export function libelleFormat(r: Reference): { nom: string; poids: string | null } {
  const g = (r.gamme ?? "").trim();
  const c = (r.conditionnement ?? "").trim();
  if (!g) return { nom: c || r.ref, poids: null };
  const dedans = sansAccent(g).toLowerCase().includes(sansAccent(c).toLowerCase());
  return { nom: g, poids: c && !dedans ? c : null };
}
